// @ts-nocheck
import React, { useCallback, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import MenuCoursesSkeleton from "./MenuCoursesSkeleton";
import { MenuContentCourses } from "./MenuContentCourses";
import { MenuContentEmployeesOverview } from "./MenuContentEmployeesOverview";
import { Routes } from "../../Routes";
import sidebarStyles from "./SidebarLayout.module.css";
import SidebarUserFooter from "./SidebarUserFooter";
import { useMenuContext } from "./MenuContext";
import { keys } from "../../strings";
import type { Course, LeftMenuProps } from "./Menu.types";
import { sidebarIcons } from "./sidebarIcons";
import { useTranslation } from "react-i18next";
import ErrorMessage from "../../Components/ErrorMessage/ErrorMessage";
import SectionTitleRow from "./SectionTitleRow";
import SidebarSection from "./SidebarSection";
import styles from "./LeftMenu.module.css";
import { useMenuEducations } from "./useMenuEducations";

export function LeftMenu({
  availableCourses = [],
  showAdmin = false,
  course,
  onSelectCourse,
  hideMenu,
  isFloatingDrawer = false,
}: LeftMenuProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const { sidebarCompact, setSidebarCollapsed } = useMenuContext();

  const expandSidebarIfCompact = useCallback(() => {
    setSidebarCollapsed(false);
  }, [setSidebarCollapsed]);

  const handleCompactAwareNavClick = useCallback(
    (event: React.MouseEvent) => {
      if (sidebarCompact) {
        event.preventDefault();
        expandSidebarIfCompact();
        return;
      }
      hideMenu();
    },
    [sidebarCompact, expandSidebarIfCompact, hideMenu]
  );

  const [isExpiredSectionCollapsed, setIsExpiredSectionCollapsed] = useState(() => {
    const saved = localStorage.getItem(keys.localStorage.hideExpiredEducationsSection);
    return saved === "true";
  });

  const [isCertificateSectionCollapsed, setIsCertificateSectionCollapsed] =
    useState(() => {
      const saved = localStorage.getItem(keys.localStorage.hideCertificateSection);
      return saved === "true";
    });

  const isMyCertificatesPage = useMemo(
    () => location.pathname === Routes.MyCertificates,
    [location.pathname]
  );

  const {
    userCoursesError,
    retryFetchUserCourses,
    showEducationListSkeleton,
    userOngoingEducations,
    userExpiredOngoingEducations,
    userExpiredEducations,
    userCompletedEducations,
  } = useMenuEducations({
    availableCourses,
    selectedCourse: course,
    onSelectCourse,
  });

  const handleCourseClick = useCallback(
    (selected: Course) => {
      onSelectCourse(selected);
      hideMenu();
    },
    [hideMenu, onSelectCourse]
  );

  const handleToggleExpiredSection = useCallback(() => {
    const newValue = !isExpiredSectionCollapsed;
    setIsExpiredSectionCollapsed(newValue);
    localStorage.setItem(keys.localStorage.hideExpiredEducationsSection, String(newValue));
  }, [isExpiredSectionCollapsed]);

  const handleToggleCertificateSection = useCallback(() => {
    const newValue = !isCertificateSectionCollapsed;
    setIsCertificateSectionCollapsed(newValue);
    localStorage.setItem(keys.localStorage.hideCertificateSection, String(newValue));
  }, [isCertificateSectionCollapsed]);

  return (
    <>
      {userCoursesError && (
        <ErrorMessage
          message={userCoursesError}
          onRetry={retryFetchUserCourses}
        />
      )}
      {!userCoursesError && (
        <div
          className={`${styles.menuMainContent} ${
            isFloatingDrawer ? styles.menuMainContentFloatingDrawer : ""
          }`}
        >
          <nav className={sidebarStyles.primaryNav} aria-label={t("menu.primaryNav", "Huvudmeny")}>
            <SectionTitleRow
              title={t("menu.myCertificates")}
              icon={sidebarIcons.myCertificates}
              selected={isMyCertificatesPage}
              link={Routes.MyCertificates}
              compact={sidebarCompact}
              onClick={handleCompactAwareNavClick}
            />
            {showAdmin && (
              <MenuContentEmployeesOverview
                compact={sidebarCompact}
                onClick={handleCompactAwareNavClick}
              />
            )}
          </nav>

          {showEducationListSkeleton ? (
            sidebarCompact ? null : (
              <SidebarSection title={t("menu.educations")}>
                <MenuCoursesSkeleton />
              </SidebarSection>
            )
          ) : (
            <>
              <MenuContentCourses
                userEducations={userOngoingEducations}
                selectedCourse={course}
                title={t("menu.educations")}
                compact={sidebarCompact}
                onExpandSidebar={expandSidebarIfCompact}
                handleCourseClick={handleCourseClick}
              />
              {(userExpiredEducations.length > 0 ||
                userCompletedEducations.length > 0) && (
                <MenuContentCourses
                  userEducations={[
                    ...userExpiredEducations,
                    ...userCompletedEducations,
                  ]}
                  selectedCourse={course}
                  title={t("menu.certificate")}
                  hideProgress={false}
                  compact={sidebarCompact}
                  onExpandSidebar={expandSidebarIfCompact}
                  handleCourseClick={handleCourseClick}
                  isCertificateSection
                  isCollapsible
                  isCollapsed={isCertificateSectionCollapsed}
                  onToggleCollapse={handleToggleCertificateSection}
                />
              )}
              {userExpiredOngoingEducations.length > 0 && (
                <MenuContentCourses
                  userEducations={userExpiredOngoingEducations}
                  selectedCourse={course}
                  title={t("menu.expiredEducations")}
                  compact={sidebarCompact}
                  onExpandSidebar={expandSidebarIfCompact}
                  handleCourseClick={handleCourseClick}
                  isExpiredSection
                  isCollapsible
                  isCollapsed={isExpiredSectionCollapsed}
                  onToggleCollapse={handleToggleExpiredSection}
                />
              )}
            </>
          )}

          <div className={sidebarStyles.sidebarFooter}>
            <SidebarUserFooter onNavigate={hideMenu} />
          </div>
        </div>
      )}
    </>
  );
}
